import React from "react";
import Modal from "react-modal";
import toast from "react-hot-toast";

import { Oval } from "react-loader-spinner";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import {
  setShowStaxPaymentModal,
  chargeCardStax,
  markPaidStax,
  sendEmailReceipt,
  resetPaymentsState,
  getOrderForPayment,
} from "../../paymentsSlice";
import { getOwesReport } from "../../../Reports/reportsSlice";
import { InvoiceStatus, PaymentType, invoiceTypeToText } from "../../../../models";
import {
  ServiceChargeToggle,
  CustomerSearch,
  PaymentMethodSelect,
  NewCardForm,
} from "../../components";

import { ReactComponent as Close } from "../../../../assets/images/close.svg";
import "./StaxPaymentModal.scss";

const modalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#FFFFFF",
    maxWidth: "500px",
    width: "90%",
    maxHeight: "90vh",
    overflow: "auto",
  },
  overlay: {
    backgroundColor: "rgba(255, 255, 255, 0.9)",
  },
};

enum PaymentMethod {
  EXISTING = "existing",
  NEW = "new",
}

export const StaxPaymentModal: React.FC = () => {
  const dispatch = useAppDispatch();

  const showStaxPaymentModal = useAppSelector(
    (state) => state.payments.showStaxPaymentModal
  );
  const order = useAppSelector((state) => state.payments.order);
  const selectedCustomer = useAppSelector(
    (state) => state.payments.selectedCustomer
  );
  const selectedPaymentMethod = useAppSelector(
    (state) => state.payments.selectedPaymentMethod
  );
  const chargeCardAPI = useAppSelector((state) => state.payments.chargeCardAPI);
  const getOrderAPI = useAppSelector((state) => state.payments.getOrderAPI);

  const [paymentMethod, setPaymentMethod] = React.useState<PaymentMethod>(
    PaymentMethod.EXISTING
  );
  const [applyServiceCharge, setApplyServiceCharge] = React.useState(false);

  const invoice = showStaxPaymentModal;
  const isOpen = !!invoice;

  // Calculate totals
  const basePrice = invoice?.total || 0;
  const totalToCharge = applyServiceCharge ? basePrice * 1.02 : basePrice;

  // Fetch order when modal opens
  React.useEffect(() => {
    if (isOpen && invoice?.orderId) {
      dispatch(getOrderForPayment(invoice.orderId));
    }
  }, [isOpen, invoice?.orderId, dispatch]);

  // Reset state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      dispatch(resetPaymentsState());
      setPaymentMethod(PaymentMethod.EXISTING);
      setApplyServiceCharge(false);
    }
  }, [isOpen, dispatch]);

  const onClose = () => {
    dispatch(setShowStaxPaymentModal(undefined));
  };

  const handlePaymentSuccess = async (result?: any) => {
    if (!invoice) return;

    try {
      // Mark invoice as paid
      await dispatch(
        markPaidStax({
          id: invoice.invoiceId,
          status: InvoiceStatus.PAID,
          paymentType: PaymentType.CARD,
        })
      ).unwrap();

      // Send email receipt if we have a transaction ID
      const transactionId = result?.id || result?.transactionId;
      if (transactionId) {
        dispatch(sendEmailReceipt(transactionId));
      }

      toast.success("Payment processed successfully!");

      // Close modal and refresh report
      onClose();
      dispatch(getOwesReport());
    } catch (error) {
      toast.error("Payment processed but failed to update invoice status");
    }
  };

  const handlePaymentError = (error: string) => {
    toast.error(error || "Payment failed. Please try again.");
  };

  const handleChargeExistingCard = async () => {
    if (!invoice || !selectedPaymentMethod) return;

    try {
      const result = await dispatch(
        chargeCardStax({
          paymentMethodId: selectedPaymentMethod.id,
          po: "",
          referenceId: invoice.orderId,
          basePrice: basePrice,
          applyServiceCharge: applyServiceCharge,
          total: totalToCharge,
          invoiceId: invoice.invoiceId,
        })
      ).unwrap();

      if (result.success) {
        handlePaymentSuccess(result);
      } else {
        handlePaymentError(result.errorMessage || "Payment failed");
      }
    } catch (error: any) {
      handlePaymentError(error?.detail || "Payment failed. Please try again.");
    }
  };

  const renderInvoiceDetails = () => {
    if (!invoice) return null;

    return (
      <div className="invoice-details">
        <div className="detail-row">
          <span className="detail-label">Customer:</span>
          <span className="detail-value">{invoice.name}</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Invoice Type:</span>
          <span className="detail-value">{invoiceTypeToText[invoice.type]}</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Amount:</span>
          <span className="detail-value">${invoice.total.toFixed(2)}</span>
        </div>
      </div>
    );
  };

  const renderPaymentMethodTabs = () => {
    return (
      <div className="payment-method-tabs">
        <div
          className={`tab ${paymentMethod === PaymentMethod.EXISTING ? "active" : ""}`}
          onClick={() => setPaymentMethod(PaymentMethod.EXISTING)}
        >
          Existing Card
        </div>
        <div
          className={`tab ${paymentMethod === PaymentMethod.NEW ? "active" : ""}`}
          onClick={() => setPaymentMethod(PaymentMethod.NEW)}
        >
          New Card
        </div>
      </div>
    );
  };

  const renderExistingCardFlow = () => {
    return (
      <>
        <CustomerSearch onCustomerSelect={() => {}} />

        {selectedCustomer && (
          <PaymentMethodSelect
            customerId={selectedCustomer.id}
            onPaymentMethodSelect={() => {}}
            onChargeClick={handleChargeExistingCard}
            chargeDisabled={!selectedPaymentMethod}
            chargeLoading={chargeCardAPI.loading}
            totalToCharge={totalToCharge}
          />
        )}
      </>
    );
  };

  const renderNewCardFlow = () => {
    if (!invoice) return null;

    // Wait for order to load to ensure we have phone/email
    if (getOrderAPI.loading || !order) {
      return (
        <div className="loading-order">
          <Oval color="#007AFF" height={24} width={24} />
          <span>Loading order details...</span>
        </div>
      );
    }

    return (
      <NewCardForm
        invoice={invoice}
        order={order}
        totalToCharge={totalToCharge}
        applyServiceCharge={applyServiceCharge}
        onPaymentSuccess={handlePaymentSuccess}
        onPaymentError={handlePaymentError}
      />
    );
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} style={modalStyles}>
      <div className="stax-payment-modal-component">
        <div className="header-container">
          <div className="header-title">Charge Card</div>
          <div className="close-container">
            <Close className="close-button" onClick={onClose} />
          </div>
        </div>

        {renderInvoiceDetails()}

        <ServiceChargeToggle
          applyServiceCharge={applyServiceCharge}
          basePrice={basePrice}
          onChange={setApplyServiceCharge}
        />

        {renderPaymentMethodTabs()}

        <div className="payment-content">
          {paymentMethod === PaymentMethod.EXISTING && renderExistingCardFlow()}
          {paymentMethod === PaymentMethod.NEW && renderNewCardFlow()}
        </div>
      </div>
    </Modal>
  );
};
